import React, { Component } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { RECURRENCE_CONFLICT_TYPES } from "@constants";
import Modal from "../../Modal/Modal";
import Loader from "../../Loader/Loader";

class ConflictConfirmationModal extends Component {
	constructor(props) {
		super(props);

		this.state = { loading: false, message: "", question: "" };
	}

	componentDidUpdate({ conflictProps: { show: prevShow } }) {
		const {
			conflictProps: { show },
		} = this.props;
		if (prevShow !== show && show) this.setMessageAndQuestion();
	}

	/** Sets the question and prompt to the current user based on the type of conflict the API has responded with when attempting to assign an officer */
	setMessageAndQuestion = () => {
		const { lang } = global;
		const {
			conflictProps: { conflictType, userScheduleConflictOverride },
		} = this.props;
		let message = "";
		let question = "";

		switch (conflictType) {
			case 1: {
				message = lang.t(
					"This officer does not qualify to be assigned to jobs on these dates",
				);
				question = lang.t("Do you want to assign only to jobs that qualify_");
				break;
			}
			case 2:
			case 3:
			case 4: {
				message = lang.t("This officer is already assigned to another jobs on these dates");
				question = lang.t("Are you sure you want to reassign_");
				break;
			}
			case 8:
			case 9:
			case 10: {
				message = lang.t(
					"Officer cannot be assigned to this job series due to a conflict with existing schedule assignments.",
				);
				question = userScheduleConflictOverride
					? lang.t("Do you want to assign the officer to the job series anyway_")
					: "";
				break;
			}
			default:
				break;
		}

		this.setState({ message, question });
	};

	onExited = () => this.setState({ loading: false, message: "", question: "" });

	/**
	 * Confirms the current user's intent to resolve the scheduling conflict and passes the necessary data to update
	 * assignments as a parameter of the prop `onConfirm`
	 */
	resolveConflict = () => {
		const {
			conflictProps: {
				userId,
				AssignmentRepeatsOn,
				updateAllUpcoming,
				ignoreMaxWeeklyHrs,
				conflictType,
				userScheduleConflictOverride,
			},
			onConfirm,
		} = this.props;

		const params = {
			userId,
			AssignmentRepeatsOn,
			updateAllUpcoming,
			ignoreMaxWeeklyHrs,
			userScheduleConflictOverride,
		};
		const collisionTypes = [
			RECURRENCE_CONFLICT_TYPES.FUTURE_JOB_COLLISIONS.value,
			RECURRENCE_CONFLICT_TYPES.CURRENT_JOB_COLLISIONS.value,
			RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_JOB_COLLISIONS.value,
		];

		const userScheduleCollisionTypes = [
			RECURRENCE_CONFLICT_TYPES.CURRENT_USER_SCHEDULE_COLLISIONS.value,
			RECURRENCE_CONFLICT_TYPES.FUTURE_USER_SCHEDULE_COLLISIONS.value,
			RECURRENCE_CONFLICT_TYPES.CURRENT_FUTURE_USER_SCHEDULE_COLLISIONS.value,
		];

		if (conflictType === RECURRENCE_CONFLICT_TYPES.CANNOT_ASSIGN_FUTURE_JOBS.value) {
			params.SkipFutureJobAssignmentErrors = true;
		} else if (collisionTypes.includes(conflictType)) {
			params.OverRuleFutureJobAssignments = true;
			params.OverRuleAssignment = true;
		} else if (
			userScheduleCollisionTypes.includes(conflictType) &&
			userScheduleConflictOverride
		) {
			params.userScheduleConflictOverride = true;
		}
		try {
			this.setState({ loading: true });
			onConfirm(params);
		} catch (err) {
			this.setState({ loading: false });
		}
	};

	render() {
		const { lang } = global;
		const { loading, message, question } = this.state;
		const {
			conflictProps: { show, jobEvents, userScheduleConflictOverride },
			timeZoneId,
			onClose,
		} = this.props;
		return (
			<Modal
				onExited={this.onExited}
				backdrop="static"
				show={show}
				id="conflict-confirmation"
			>
				<Modal.Header>
					<Modal.Title>{lang.t("Assignment Conflict")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<p className="col-sm-12">{message}</p>
					</div>
					<ul>
						<For
							each="item"
							of={jobEvents}
						>
							<li key={`assignment_conflict_${item.JobEvent.JobEventId}`}>
								{utils.formatDate(
									new Date(`${item.JobEvent.StartDateTime}Z`),
									"en-US",
									timeZoneId,
									"dt-short",
								)}
							</li>
						</For>
					</ul>
					<div className="row">
						<div className="col-sm-12">{question}</div>
					</div>
				</Modal.Body>
				<Choose>
					<When condition={userScheduleConflictOverride}>
						<Modal.Footer>
							<button
								type="button"
								className="btn btn-link"
								onClick={onClose}
							>
								{lang.t("Cancel")}
							</button>
							<button
								type="button"
								className="btn btn-primary"
								onClick={this.resolveConflict}
							>
								{lang.t("Continue")}
							</button>
						</Modal.Footer>
					</When>
					<Otherwise>
						<Modal.Footer>
							<button
								type="button"
								className="btn btn-link"
								onClick={onClose}
							>
								{lang.t("Cancel")}
							</button>
						</Modal.Footer>
					</Otherwise>
				</Choose>
				<Loader active={loading} />
			</Modal>
		);
	}
}

ConflictConfirmationModal.propTypes = {
	conflictProps: PropTypes.shape({
		show: PropTypes.bool,
		userId: PropTypes.number,
		AssignmentRepeatsOn: PropTypes.string,
		updateAllUpcoming: PropTypes.bool,
		ignoreMaxWeeklyHrs: PropTypes.bool,
		conflictType: PropTypes.any,
		jobEvents: PropTypes.array,
		userScheduleConflictOverride: PropTypes.bool,
	}),
	timeZoneId: PropTypes.string,
	onConfirm: PropTypes.func,
	onClose: PropTypes.func,
};

ConflictConfirmationModal.defaultProps = {
	conflictProps: {
		show: false,
		userId: undefined,
		AssignmentRepeatsOn: "",
		updateAllUpcoming: false,
		ignoreMaxWeeklyHrs: false,
		conflictType: undefined,
		jobEvents: [],
		userScheduleConflictOverride: false,
	},
	timeZoneId: undefined,
	onConfirm: () => {},
	onClose: () => {},
};

export default ConflictConfirmationModal;
